import { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { Pencil } from 'lucide-react';

import { DataTable, PageHeader, SearchInput, Skeleton } from '../../components';
import { createLink, formatMoney, searchFn } from '../../utils';
import { useBranch } from '../../hooks';
import { ROUTE_URLS } from '../../routes';
import { useI18n } from '../../i18n';

export const BranchPrices = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { data: branch, isLoading, isError } = useBranch(id);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (isError) {
      navigate(ROUTE_URLS.BRANCHES);
    }
  }, [isError, navigate]);

  const columns = useMemo(
    () => [
      {
        title: t('products.name'),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: name => (
          <div className='wm-cell-main'>
            <span className='wm-cell-icon'>{name.slice(0, 2)}</span>
            <span className='wm-strong'>{name}</span>
          </div>
        ),
      },
      {
        title: t('branches.sellPrice'),
        dataIndex: 'sell_price',
        align: 'right',
        width: 220,
        render: sellPrice => <span className='wm-strong'>{formatMoney(sellPrice)}</span>,
        sorter: (a, b) => a.sell_price - b.sell_price,
      },
    ],
    [t],
  );

  const filteredProducts = useMemo(
    () => searchFn(branch?.products, searchText),
    [branch, searchText],
  );

  return (
    <>
      <PageHeader
        backTo={ROUTE_URLS.BRANCHES}
        backLabel={t('nav.branches')}
        title={
          isLoading ? <Skeleton width={260} height={26} /> : t('branches.pricesTitle', { branch: branch?.name })
        }
        subtitle={t('branches.pricesSubtitle')}
        actions={
          <Link to={createLink(ROUTE_URLS.EDIT_BRANCH_PRICES, id)}>
            <Button type='primary' icon={<Pencil size={15} />}>
              {t('branches.editPrices')}
            </Button>
          </Link>
        }
      />
      <div className='wm-toolbar'>
        <SearchInput value={searchText} onChange={setSearchText} placeholder={t('products.searchPlaceholder')} />
        <span className='wm-toolbar__grow' />
        {branch?.products && (
          <span className='wm-badge wm-badge--plain'>
            {t('products.count', { count: branch.products.length })}
          </span>
        )}
      </div>
      <DataTable
        dataSource={filteredProducts}
        columns={columns}
        isLoading={isLoading}
        emptyVariant={searchText ? 'search' : undefined}
      />
    </>
  );
};
